import React from "react";
import { Card, Typography } from "antd";
import { MedicardTemplateProps } from "pages/MediCard/types";
import "./CountyCareTemplate.css";

const { Title, Text, Link } = Typography;

const CountyCareTemplate: React.FC<MedicardTemplateProps> = ({ data, logo, t }) => {

  const toHref = (url: string) =>
    url?.startsWith("http") ? url : `https://${url}`;
  return (
    <div className="hc-medicard-countycare">
      <div className="card-container" style={{ textAlign: "left" }}>
      {/* FRONT SIDE */}
      <Card
        className="countycare-card"
        bordered={false}
        style={{ border: "2px solid #000", borderRadius: "20px" }}
      >
        <img
          src={logo}
          alt={t("MEDICARD.LOGO_ALT", { defaultValue: "Health plan logo" })}
          className="logo"
          style={{
            display: "block",
            margin: "0 auto",
            width: "auto",
            height: "60px",
          }}
        />

        <hr className="blue-line" />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "5%",
            alignItems: "flex-end",
          }}
        >
          {/* Left */}
          <div className="card-left">
            <div className="member-section">
              <div className="field">
                <Text className="label">{t("MEDICARD.MEMBER_NAME")}</Text>
                <Text>{data.PatientName}</Text>
              </div>
              <div className="field">
                <Text className="label">{t("MEDICARD.MEDICAID_ID")}</Text>
                <Text>{data.MedicaidID}</Text>
              </div>
              <div className="field">
                <Text className="label">{t("MEDICARD.PRODUCT")}</Text>
                <Text>{data.Product}</Text>
              </div>
            </div>

            <div className="pcp-section">
              <div className="field">
                <Text className="label">{t("MEDICARD.PCP_NAME")}</Text>
                <Text>{data.PCPName}</Text>
              </div>
              <div className="field">
                <Text className="label">{t("MEDICARD.PCP_PHONE")}</Text>
                <Text>{data.PCPPhone}</Text>
              </div>
            </div>

            <div className="rx-info">
              <Text>
                <span className="label">{t("MEDICARD.RX")}</span> {data.RX}
              </Text>
              <br />
              <Text>
                <span className="label">{t("MEDICARD.RX_BIN")}</span> {data.RXBin}
              </Text>
              <br />
              <Text>
                <span className="label">{t("MEDICARD.RX_PCN")}</span> {data.RXPCN}
              </Text>
              <br />
              <Text>
                <span className="label">{t("MEDICARD.RX_GROUP")}</span> {data.RXGroup}
              </Text>
            </div>

            <Text className="reg-agency">
              <strong>{t("MEDICARD.REGULATORY")}</strong>
            </Text>
          </div>

          {/* Right */}
          <div className="card-right">
            <div className="member-service-box">
              <Text className="heading">{t("MEDICARD.MEMBER_SERVICES")}</Text>
              <br />
              <Text>{data.MedicardMemberServicesPhone}</Text>
              <br />
              <Text>{data.MedicardMemberServicesTollFree} (toll-free)</Text>
              <br />
              <Text>{data.MedicardMemberServicesTTY} (TDD/TTY)</Text>
              <br />
             <Link href={toHref(data.MedicardMemberServicesWebsite)} target="_blank" rel="noopener noreferrer" aria-label={`${data.MedicardMemberServicesWebsite} (${t("GLOBAL.OPENS_NEW_TAB", { defaultValue: "opens in new tab" })})`}>
                  {data.MedicardMemberServicesWebsite}
                </Link>
            </div>
            <div className="effective">
              <Text>
                <span className="label">{t("MEDICARD.EFFECTIVE_DATE")}</span> {data.MedicaidEffectiveDate}
              </Text>
            </div>
            <div className="copay" style={{ color: "#139ad2" }}>
              <Text strong style={{ color: "#139ad2" }}>
                {t("MEDICARD.NO_COPAYS")}
              </Text>
            </div>
          </div>
        </div>
      </Card>

      {/* BACK SIDE */}
      <Card
        className="back-card"
        bordered={false}
        style={{ border: "2px solid #000", borderRadius: "20px" }}
      >
        <div className="back-section">
          <Title level={5} className="section-title">
            {t("MEDICARD.MEMBERS")}
          </Title>
          <div className="back-member-section">
            <Text>
              <strong>For emergencies,</strong> call 9-1-1 or go to the nearest
              emergency department.
            </Text>

            <Text>
             <strong>For the 24/7 Nurse Advice Line,</strong> call{" "}
                {data.MedicardNurseAdviceLinePhone}, {data.MedicardNurseAdviceLineTollFree} (toll-free),{" "}
                {data.MedicardNurseAdviceLineTTY} (TDD/TTY).
            </Text>

            <Text>
             <strong>For questions about {data.MedicardSupportedServices},</strong> call{" "}
                {data.MedicardMemberServicesPhone}, {data.MedicardSupportTollFreePhone} (toll-free),{" "}
                {data.MedicardSupportTTYPhone} (TDD/TTY).
            </Text>
          </div>

          <hr className="blue-line" />
          <Title level={5} className="section-title">
            {t("MEDICARD.PROVIDERS")}
          </Title>

          <div
            className="provider-row"
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 24,
            }}
          >
            <div className="provider-left">
              <Text>
                <strong> {t("MEDICARD.SUBMIT_CLAIMS_TO")}</strong>
              </Text>

              <Text>
                 {data.MedicardClaimPayName}<br />
                  Attention: {data.MedicardClaimAttention}<br />
                  {data.MedicardClaimAddress1}<br />
                  {data.MedicardClaimAddress2 && <>{data.MedicardClaimAddress2}<br /></>}
                  {data.MedicardClaimsState} {""}
                  {data?.MedicardClaimsZipCode}
              </Text>

              <Text className="payerId">
                <strong>{t("MEDICARD.PAYROLL_ID")}</strong> {data.MedicardPayorId}
              </Text>
            </div>

            <div className="provider-right">
              <Text>
                   <strong>{data.MedicardProviderSupportedServices}</strong><br />
                  {data.MedicardProviderSupportPhone}
              </Text>

              <Text>
                <strong> {t("MEDICARD.PROVIDER_PORTAL")}</strong>{" "}
                <Link href={toHref(data.MedicardProviderPortalWebsite)} target="_blank" rel="noopener noreferrer" aria-label={`${data.MedicardProviderPortalWebsite} (${t("GLOBAL.OPENS_NEW_TAB", { defaultValue: "opens in new tab" })})`}>
                    {data.MedicardProviderPortalWebsite}
                  </Link>
              </Text>
            </div>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
};

export default CountyCareTemplate;
